import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Printer, SignOut, User, CaretDown,
  Sparkle, Scissors, VideoCamera,
  IdentificationCard, BookOpen, Flag, Package, TShirt, Gift, PencilSimple, Certificate,
} from "@phosphor-icons/react";

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const onLogout = () => { logout(); nav("/"); };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[var(--pf-border)]">
      <div className="pf-cmyk-bar"><span/><span/><span/><span/></div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <Printer size={22} weight="fill" />
          <span className="font-black text-lg tracking-tighter">TALAIO<span className="text-[var(--pf-brand)]">/AI</span></span>
        </Link>
        <nav className="flex items-center gap-1 md:gap-4">
          {user ? (
            <>
              {user.role === "client" && (
                <>
                  <Link to="/dashboard" data-testid="nav-dashboard" className={`hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)] ${loc.pathname==="/dashboard"?"text-[var(--pf-brand)]":""}`}>Dashboard</Link>
                  <Link to="/orders" data-testid="nav-orders" className={`hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)] ${loc.pathname==="/orders"?"text-[var(--pf-brand)]":""}`}>Orders</Link>
                  <Link to="/brief/new" data-testid="nav-new-brief" className="pf-btn-primary text-sm">+ New Brief</Link>
                </>
              )}
              {user.role === "designer" && (
                <Link to="/designer" data-testid="nav-designer" className="hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)]">My Briefs</Link>
              )}
              {user.role === "vendor" && (
                <Link to="/vendor" data-testid="nav-vendor" className="hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)]">My Orders</Link>
              )}
              {user.role === "admin" && (
                <>
                  <Link to="/admin" data-testid="nav-admin" className="hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)]">Overview</Link>
                  <Link to="/admin/assignments" data-testid="nav-admin-assign" className="hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)]">Assign</Link>
                  <Link to="/admin/orders" data-testid="nav-admin-orders" className="hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)]">Orders</Link>
                  <Link to="/admin/customers" data-testid="nav-admin-customers" className="hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)]">Customers</Link>
                  <Link to="/admin/applications" data-testid="nav-admin-applications" className="hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)]">Inbox</Link>
                  <Link to="/admin/vendors" data-testid="nav-admin-vendors" className="hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)]">Vendors</Link>
                  <Link to="/admin/whatsapp" data-testid="nav-admin-whatsapp" className="hidden md:block px-3 py-2 text-sm font-medium hover:text-[var(--pf-brand)]">WhatsApp</Link>
                </>
              )}
              <div className="flex items-center gap-2 pl-3 border-l border-[var(--pf-border)] ml-2">
                <div className="hidden md:flex items-center gap-2 text-sm">
                  <User size={16} />
                  <span data-testid="nav-user-name" className="font-medium">{user.name}</span>
                </div>
                <button onClick={onLogout} data-testid="nav-logout" className="p-2 hover:bg-[var(--pf-surface-2)]" title="Logout">
                  <SignOut size={18} />
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/logo-studio" data-testid="nav-free-logo" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] hover:opacity-70 transition-opacity">
                <Sparkle size={13} weight="fill"/> Free Logo
              </Link>
              <Link to="/startup-kit" data-testid="nav-startup-kit" className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)] hover:text-[var(--pf-brand)] transition-colors">
                Startup Kit
              </Link>
              <Link to="/login" data-testid="nav-login" className="px-4 py-2 text-sm font-medium">Log in</Link>
              <Link to="/signup" data-testid="nav-signup" className="pf-btn-primary text-sm">Get Started</Link>
            </>
          )}
        </nav>
      </div>

      {/* Secondary sub-nav — Dielines · Brand Video · Products — shown to everyone */}
      <SubNav />
    </header>
  );
}

// -----------------------------------------------------------------------------
// Secondary sub-nav strip — Dielines, Brand Video, Products dropdown
// -----------------------------------------------------------------------------
function SubNav() {
  const loc = useLocation();
  const isActive = (path) => loc.pathname === path || loc.pathname.startsWith(path + "/");

  return (
    <div className="border-t border-[var(--pf-border)] bg-white overflow-visible" data-testid="sub-nav">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 overflow-visible">
        <div className="flex items-center gap-1 md:gap-2 flex-wrap py-1 overflow-visible">
          <Link
            to="/menu-design"
            data-testid="nav-ai-designs"
            className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap text-sm font-semibold transition-colors relative ${
              isActive("/menu-design") ? "text-[var(--pf-brand)]" : "text-[var(--pf-text-2)] hover:text-[var(--pf-text)]"
            }`}
          >
            <Sparkle size={16} weight={isActive("/menu-design") ? "fill" : "regular"}/>
            AI Designs
            {isActive("/menu-design") && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[var(--pf-brand)]"/>}
          </Link>
          <Link
            to="/dielines"
            data-testid="nav-dielines"
            className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap text-sm font-semibold transition-colors relative ${
              isActive("/dielines") ? "text-[var(--pf-brand)]" : "text-[var(--pf-text-2)] hover:text-[var(--pf-text)]"
            }`}
          >
            <Scissors size={16} weight={isActive("/dielines") ? "fill" : "regular"}/>
            Dielines
            {isActive("/dielines") && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[var(--pf-brand)]"/>}
          </Link>
          <Link
            to="/brand/video"
            data-testid="nav-brand-video"
            className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap text-sm font-semibold transition-colors relative ${
              isActive("/brand/video") ? "text-[var(--pf-brand)]" : "text-[var(--pf-text-2)] hover:text-[var(--pf-text)]"
            }`}
          >
            <VideoCamera size={16} weight={isActive("/brand/video") ? "fill" : "regular"}/>
            Brand Video
            {isActive("/brand/video") && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[var(--pf-brand)]"/>}
          </Link>
          <ProductsDropdown />
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Products dropdown — 8 print product categories
// -----------------------------------------------------------------------------
const PRODUCT_CATS = [
  { slug: "visiting-cards",   label: "Visiting Cards",  icon: IdentificationCard, blurb: "Premium 350gsm, 300+ styles" },
  { slug: "brochures",        label: "Brochures",       icon: BookOpen,          blurb: "Bi-fold, tri-fold, gate-fold" },
  { slug: "hoardings",        label: "Hoardings",       icon: Flag,              blurb: "Outdoor banners & flex" },
  { slug: "packaging",        label: "Packaging",       icon: Package,           blurb: "Retail cartons & mailers" },
  { slug: "apparel",          label: "Apparel",         icon: TShirt,            blurb: "T-shirts, hoodies, caps" },
  { slug: "corporate-gifts",  label: "Corporate Gifts", icon: Gift,              blurb: "Diaries, mugs, hampers" },
  { slug: "stationery",       label: "Stationery",      icon: PencilSimple,      blurb: "Letterhead, envelopes, invoices" },
  { slug: "certificates",     label: "Certificates",    icon: Certificate,       blurb: "Foil, embossing, seals" },
];

function ProductsDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div
      className="relative"
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        onClick={() => setOpen(!open)}
        data-testid="nav-products-toggle"
        className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap text-sm font-semibold transition-colors ${open ? "text-[var(--pf-brand)]" : "text-[var(--pf-text-2)] hover:text-[var(--pf-text)]"}`}
      >
        <Package size={16} weight={open ? "fill" : "regular"}/>
        Products
        <CaretDown size={12} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div
          data-testid="nav-products-menu"
          className="absolute left-0 top-full w-[520px] bg-white border border-[var(--pf-border)] rounded-lg shadow-xl p-2 z-50"
        >
          <div className="grid grid-cols-2 gap-1">
            {PRODUCT_CATS.map(({ slug, label, icon: Icon, blurb }) => (
              <Link
                key={slug}
                to={`/products/${slug}`}
                onClick={() => setOpen(false)}
                data-testid={`nav-product-${slug}`}
                className="flex items-start gap-3 px-3 py-2.5 rounded hover:bg-[var(--pf-surface-2)] transition-colors group"
              >
                <Icon size={20} weight="duotone" className="text-[var(--pf-brand)] mt-0.5 shrink-0"/>
                <div>
                  <div className="font-bold text-sm group-hover:text-[var(--pf-brand)] transition-colors">{label}</div>
                  <div className="text-[11px] text-[var(--pf-text-2)] leading-tight">{blurb}</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="border-t border-dashed border-[var(--pf-border)] mt-2 pt-2 px-3 pb-1">
            <Link
              to="/brief/new"
              onClick={() => setOpen(false)}
              data-testid="nav-product-custom"
              className="flex items-center justify-between text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] hover:opacity-70"
            >
              <span>Custom brief — talk to a designer</span>
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export { PRODUCT_CATS };
